// recovery-guards.ts
//
// Orchestrator recovery (3.40.0): may the controller write a recovery ticket
// for THIS trigger? PURE over the journaled recovery ops (never process
// memory — a replay must count the same). Evaluated only for T1–T3.
//
// Order: other ⇒ ignore · off ⇒ disabled (no journal at all) · trigger_seq
// already applied/skipped by ANY op id ⇒ settled · intent without result ⇒
// resume · not_eligible > repeat_trigger > cap_reached · the next try number
// reaches 3 ⇒ materialize_failed · else create try n (0, 1, 2). The try number
// is the op id's `-r<n>` suffix (highest + 1), not the count of `failed`
// intents: a try that threw before its intent leaves a gap, and counting would
// hand out the same op id twice (review G2 H1).
// Only `applied` ticket ops with a ticket id count toward cap and repeat.
import type { RecoveryTrigger, RecoveryTriggerVerdict } from "./recovery-trigger-classifier.js";

export const RECOVERY_TICKET_OP_KIND = "orchestrator_recovery_ticket";
export const RECOVERY_SKIP_OP_KIND = "orchestrator_recovery_skipped";

export const RECOVERY_SKIP_REASONS = [
  "cap_reached",
  "repeat_trigger",
  "not_eligible",
  "materialize_failed",
] as const;

export type RecoverySkipReason = (typeof RECOVERY_SKIP_REASONS)[number];

/** `status --json` `recoveries[].result` (D2.9, N27). */
export const RECOVERY_STATUS_RESULTS = [
  "applied",
  "pending",
  ...RECOVERY_SKIP_REASONS.map((r) => `skipped:${r}` as const),
] as const;

export type RecoveryStatusResult = (typeof RECOVERY_STATUS_RESULTS)[number];

/** Original try + `-r1` + `-r2`. */
export const MAX_RECOVERY_TRIES_PER_TRIGGER = 3;

/** One journaled recovery `operation_intent` (+ its result, when there is one). */
export interface RecoveryOpRecord {
  operation_id: string;
  operation_kind: typeof RECOVERY_TICKET_OP_KIND | typeof RECOVERY_SKIP_OP_KIND;
  source_ticket: string;
  trigger: RecoveryTrigger;
  trigger_seq: number;
  /** Recovery ticket id (ticket ops only). */
  ticket_id?: string;
  /** Skip reason (skip ops only). */
  reason?: RecoverySkipReason;
  /** Undefined ⇒ intent without result (crash mid-way). */
  result?: "applied" | "failed" | "skipped";
  intent_seq: number;
  /** Journal seq of the result (B19: `run --json` lists a stop whose recovery landed in this run). */
  result_seq?: number;
}

export interface RecoveryGuardInput {
  /** `enabled` ∧ cap > 0 (orchestrator-recovery-config.ts). */
  active: boolean;
  maxRecoveries: number;
  verdict: RecoveryTriggerVerdict;
  triggerSeq: number;
  ledger: readonly RecoveryOpRecord[];
}

export type RecoveryGuardVerdict =
  | { action: "ignore" }
  | { action: "disabled" }
  | { action: "settled" }
  | { action: "resume"; pending: RecoveryOpRecord; attempt: number }
  | { action: "skip"; reason: RecoverySkipReason }
  | { action: "create"; attempt: number; priorTicketId?: string };

/** `-r<n>` ⇒ n, the original try ⇒ 0 (op ids end in `-<trigger_seq>` otherwise). */
export function recoveryTryNumber(op: Pick<RecoveryOpRecord, "operation_id">): number {
  const match = /-r(\d+)$/.exec(op.operation_id);
  return match === null ? 0 : Number(match[1]);
}

/** Ticket ops that really produced a recovery ticket. */
export function appliedRecoveries(ledger: readonly RecoveryOpRecord[]): RecoveryOpRecord[] {
  return ledger.filter(
    (r) =>
      r.operation_kind === RECOVERY_TICKET_OP_KIND &&
      r.result === "applied" &&
      r.ticket_id !== undefined,
  );
}

export function evaluateRecoveryGuards(input: RecoveryGuardInput): RecoveryGuardVerdict {
  const { verdict, ledger } = input;
  if (verdict.trigger === "other") return { action: "ignore" };
  if (!input.active) return { action: "disabled" };

  const sameSeq = ledger.filter((r) => r.trigger_seq === input.triggerSeq);
  if (sameSeq.some((r) => r.result === "applied" || r.result === "skipped")) {
    return { action: "settled" };
  }
  const tries = sameSeq
    .filter((r) => r.operation_kind === RECOVERY_TICKET_OP_KIND)
    .sort((a, b) => recoveryTryNumber(a) - recoveryTryNumber(b));
  const pending = tries.find((r) => r.result === undefined);
  if (pending !== undefined) {
    return { action: "resume", pending, attempt: recoveryTryNumber(pending) };
  }

  const applied = appliedRecoveries(ledger);
  if (!verdict.eligible) return { action: "skip", reason: "not_eligible" };
  if (applied.some((r) => r.trigger === verdict.trigger)) {
    return { action: "skip", reason: "repeat_trigger" };
  }
  if (applied.length >= input.maxRecoveries) return { action: "skip", reason: "cap_reached" };
  const last = tries.at(-1);
  const next = last === undefined ? 0 : recoveryTryNumber(last) + 1;
  if (next >= MAX_RECOVERY_TRIES_PER_TRIGGER) {
    return { action: "skip", reason: "materialize_failed" };
  }
  const prior = last?.ticket_id;
  return {
    action: "create",
    attempt: next,
    ...(prior === undefined ? {} : { priorTicketId: prior }),
  };
}
